import { Trans, useTranslation } from 'react-i18next';
import { SvgIcon } from '@progress/kendo-react-common';
import { plusIcon } from '@progress/kendo-svg-icons';
import { DlpProjectModel, DlpQuotaProfileRowModel, DlpScopeModel } from 'services/DlpConfiguration/DlpConfigurationService';
import { DlpIssue, profileKey } from 'utils/dlpConfigurationValidation';
import { IssueBadges } from '../Common/IssueBadges';
import { ProfileDetail } from './ProfileDetail';

interface ProfilesPanelProps {
    project: DlpProjectModel;
    // Profile rows and scopes of the current project only
    profiles: DlpQuotaProfileRowModel[];
    scopes: DlpScopeModel[];
    issues: DlpIssue[];
    selectedName: string | undefined;
    onSelect: (name: string) => void;
    onCreate: () => Promise<void>;
    onSave: (currentName: string, newName: string, rows: DlpQuotaProfileRowModel[]) => Promise<void>;
    onDuplicate: (name: string) => Promise<void>;
    onDelete: (name: string) => Promise<void>;
    onOpenScope: (scopeId: number) => void;
    onDirtyChange: (dirty: boolean) => void;
}

export function ProfilesPanel({ project, profiles, scopes, issues, selectedName, onSelect, onCreate, onSave, onDuplicate, onDelete, onOpenScope, onDirtyChange }: ProfilesPanelProps) {
    const { t } = useTranslation();
    const names = Array.from(new Set(profiles.map(p => p.profile_name)));
    const name = selectedName && names.includes(selectedName) ? selectedName : names[0];

    const rowsFor = (n: string) => profiles.filter(p => p.profile_name === n);
    const issuesFor = (n: string) => issues.filter(i => i.targetId === profileKey(project.id, n));

    return (
        <div className="two-col">
            <div className="list-col quota-profiles-list">
                <button type="button" className="lm-btn-primary full" onClick={onCreate}>
                    <SvgIcon icon={plusIcon} /> <Trans>dlpConfiguration.profiles.newProfile</Trans>
                </button>
                {names.map(n => (
                    <button type="button" key={n} aria-pressed={n === name} className={`list-item ${n === name ? 'active' : ''}`} onClick={() => n !== name && onSelect(n)}>
                        <span className="list-item-title" title={n}>{n}</span>
                        <span className="list-item-sub">{t('dlpConfiguration.profiles.rowCount', { count: rowsFor(n).length })}</span>
                        <span className="list-item-badges"><IssueBadges issues={issuesFor(n)} compact /></span>
                    </button>
                ))}
                {names.length === 0 && <div className="empty-note">{t('dlpConfiguration.profiles.emptyProject')}</div>}
            </div>

            {name && (
                <ProfileDetail
                    key={name}
                    project={project}
                    name={name}
                    rows={rowsFor(name)}
                    otherNames={names.filter(n => n !== name)}
                    referencingScopes={scopes.filter(s => s.quota_profile === name)}
                    issues={issuesFor(name)}
                    onSave={onSave}
                    onDuplicate={onDuplicate}
                    onDelete={onDelete}
                    onOpenScope={onOpenScope}
                    onDirtyChange={onDirtyChange}
                />
            )}
        </div>
    );
}
